import { InputBaseComponentProps } from '@mui/material/InputBase';
import { useForkRef } from '@mui/material/utils';
import classNames from 'classnames';
import {
  ChangeEvent,
  ClipboardEvent,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  MouseEvent,
  useRef,
} from 'react';

import { DEFAULT_MAX_HOURS, HOURS_PLACEHOLDER, MINUTES_PLACEHOLDER } from './constants';
import {
  completeDuration,
  isHoursSegmentFull,
  isSeparatorKey,
  joinDuration,
  splitDuration,
  toHoursSegment,
  toMinutesSegment,
} from './helpers';

import styles from './DurationInput.module.scss';

interface IDurationSegmentsProps extends InputBaseComponentProps {
  maxHours?: number;
  onValueChange?: (value: string) => void;
}

const DurationSegments = forwardRef<HTMLInputElement, IDurationSegmentsProps>(
  (
    {
      value,
      maxHours = DEFAULT_MAX_HOURS,
      onValueChange,
      onFocus,
      onBlur,
      disabled,
      className,
      'aria-labelledby': labelledBy,
    },
    ref,
  ) => {
    const hoursRef = useRef<HTMLInputElement>(null);
    const minutesRef = useRef<HTMLInputElement>(null);
    const handleHoursRef = useForkRef(ref, hoursRef);

    const duration = String(value ?? '');
    const { hours, minutes } = splitDuration(duration);

    const change = (next: string) => onValueChange?.(next);

    const changeHours = (event: ChangeEvent<HTMLInputElement>) => {
      const nextHours = toHoursSegment(event.target.value, maxHours);
      change(joinDuration({ hours: nextHours, minutes }));
      if (isHoursSegmentFull(nextHours, maxHours)) minutesRef.current?.focus();
    };

    const changeMinutes = (event: ChangeEvent<HTMLInputElement>) =>
      change(joinDuration({ hours, minutes: toMinutesSegment(event.target.value) }));

    const keyDownHours = (event: KeyboardEvent<HTMLInputElement>) => {
      const { selectionEnd, value: typed } = event.currentTarget;
      const isSeparator = isSeparatorKey(event);
      const leavesRight =
        (isSeparator && typed !== '') ||
        (event.key === 'ArrowRight' && selectionEnd === typed.length);
      if (isSeparator || leavesRight) event.preventDefault();
      if (leavesRight) minutesRef.current?.focus();
    };

    const keyDownMinutes = (event: KeyboardEvent<HTMLInputElement>) => {
      const { selectionStart, value: typed } = event.currentTarget;
      const leavesLeft =
        (event.key === 'Backspace' && typed === '') ||
        (event.key === 'ArrowLeft' && selectionStart === 0);
      if (isSeparatorKey(event)) event.preventDefault();
      if (leavesLeft) {
        event.preventDefault();
        hoursRef.current?.focus();
      }
    };

    const pasteDuration = (event: ClipboardEvent<HTMLInputElement>) => {
      const pasted = event.clipboardData.getData('text').trim();
      if (!pasted.includes(':')) return;
      event.preventDefault();
      const parts = splitDuration(pasted);
      change(
        joinDuration({
          hours: toHoursSegment(parts.hours, maxHours),
          minutes: toMinutesSegment(parts.minutes),
        }),
      );
    };

    const enterSegment = (event: FocusEvent<HTMLInputElement>) => {
      event.currentTarget.select();
      onFocus?.(event);
    };

    const leaveSegment = (event: FocusEvent<HTMLInputElement>) => {
      const target = event.relatedTarget;
      if (target === hoursRef.current || target === minutesRef.current) return;
      const completed = completeDuration({ hours, minutes });
      if (completed !== duration) change(completed);
      onBlur?.(event);
    };

    const keepSelection = (event: MouseEvent<HTMLInputElement>) => event.preventDefault();

    return (
      <div
        role="group"
        aria-labelledby={labelledBy}
        className={classNames(className, styles.segments)}
      >
        <span className={styles.sizer} data-value={hours || HOURS_PLACEHOLDER}>
          <input
            ref={handleHoursRef}
            aria-label="Hours"
            className={styles.segment}
            size={1}
            value={hours}
            placeholder={HOURS_PLACEHOLDER}
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            onChange={changeHours}
            onKeyDown={keyDownHours}
            onPaste={pasteDuration}
            onFocus={enterSegment}
            onBlur={leaveSegment}
            onMouseUp={keepSelection}
          />
        </span>
        <span className={styles.separator}>:</span>
        <span className={styles.sizer} data-value={minutes || MINUTES_PLACEHOLDER}>
          <input
            ref={minutesRef}
            aria-label="Minutes"
            className={styles.segment}
            size={1}
            value={minutes}
            placeholder={MINUTES_PLACEHOLDER}
            inputMode="numeric"
            autoComplete="off"
            disabled={disabled}
            onChange={changeMinutes}
            onKeyDown={keyDownMinutes}
            onPaste={pasteDuration}
            onFocus={enterSegment}
            onBlur={leaveSegment}
            onMouseUp={keepSelection}
          />
        </span>
      </div>
    );
  },
);

DurationSegments.displayName = 'DurationSegments';

export default DurationSegments;
